<template>
  <div>
    <div class="steps_body">
      <app-page-section :title="$t('Tipo de simulação')">
        <calc-form-card>
          <calc-form-item :title="$t('Tipo de simulação')" title-style="inline">
            <b-radio-group>
              <b-radio native-value="1" v-model.number="calculation.input.type"
                >{{ $t("input.type", 1) }}
              </b-radio>
              <b-radio native-value="2" v-model.number="calculation.input.type"
                >{{ $t("input.type", 2) }}
              </b-radio>
              <b-radio native-value="3" v-model.number="calculation.input.type"
                >{{ $t("input.type", 3) }}
              </b-radio>
              <b-radio
                v-if="user && user.isInternal"
                native-value="4"
                v-model.number="calculation.input.type"
                >{{ $t("input.type", 4) }}
              </b-radio>
            </b-radio-group>

            <template #illustration>
              <img style="margin-top: -42px" :src="typeIllustrationSrc" />
            </template>
          </calc-form-item>
        </calc-form-card>

        <calc-form-card>
          <calc-form-item
            :title="$t('Localização da obra')"
            title-style="inline"
          >
            <div class="u-df">
              <b-select
                class="u-mr8"
                v-model.number="calculation.input.location"
              >
                <option
                  v-for="country in countries"
                  :key="country.id"
                  :value="country.id"
                >
                  {{ $t("input.location", country.id) }}
                </option>
                <option :key="-1" :value="-1">Outro</option>
              </b-select>

              <b-input
                v-if="calculation.input.location == -1"
                v-model="calculation.input.country"
                placeholder="Escreva aqui o país"
              />
            </div>
          </calc-form-item>
        </calc-form-card>
      </app-page-section>

      <app-page-section :title="$t('Dados geométricos')">
        <transition name="anim-slideX">
          <calc-form-card
            v-if="calculation.input.type === 1 || calculation.input.type === 4"
          >
            <calc-form-item :title="$t('Ângulo')" title-style="inline">
              <calc-number-input
                v-model="calculation.input.inclination"
                :min="0"
                :max="90"
                addon="º"
              />

              <template #illustration>
                <calc-illustration which="angle" />
              </template>
            </calc-form-item>
          </calc-form-card>
        </transition>

        <transition name="anim-slideX">
          <calc-form-card v-if="calculation.input.type === 2">
            <calc-form-item
              :title="$t('Orientação do perfil')"
              title-style="inline"
            >
              <b-select v-model.number="calculation.input.direction">
                <option value="1">{{ $t("input.direction", 1) }}</option>
                <option value="2">{{ $t("input.direction", 2) }}</option>
              </b-select>
            </calc-form-item>
          </calc-form-card>
        </transition>

        <transition name="anim-slideX">
          <calc-form-card v-if="calculation.input.type === 2">
            <calc-form-item :title="$t('Formato')" title-style="inline">
              <b-radio-group>
                <b-radio
                  v-model.number="calculation.input.format"
                  native-value="1"
                >
                  <img class="format_img" src="@/assets/img/format_1.svg" />
                </b-radio>
                <b-radio
                  v-model.number="calculation.input.format"
                  native-value="2"
                >
                  <img class="format_img" src="@/assets/img/format_2.svg" />
                </b-radio>
              </b-radio-group>

              <template #illustration>
                <img :src="formatIllustrationSrc" />
              </template>
            </calc-form-item>
          </calc-form-card>
        </transition>

        <calc-form-card>
          <calc-form-item title-style="inline" :title="$t('Número de vãos')">
            <calc-number-input
              v-model="totalMainSpans"
              :min="1"
              :max="20"
              :step="1"
              :precision="0"
            />
          </calc-form-item>
        </calc-form-card>

        <calc-form-card>
          <template #controls>
            <b-switch
              v-model="calculation.input.hasCantilever"
              class="is-label_left"
              >{{ $t("Incluir consolas") }}</b-switch
            >
            <b-switch
              v-model="isSpanBySpan"
              :disabled="calculation.input.hasCantilever"
              class="is-label_left"
              >{{ $t("Definir valor por vão") }}</b-switch
            >
          </template>

          <calc-form-item :title-style="!isSpanBySpan ? 'inline' : 'block'">
            <template #title>
              {{ $t("Dimensão dos vãos") }}
              <app-tooltip class="m-pad-0">
                <img src="@/assets/img/dimensao_vaos.png" />
              </app-tooltip>
            </template>

            <calc-number-input
              v-if="!isSpanBySpan"
              v-model="singleSpanValue"
              :min="0.5"
              :max="20"
              addon="m"
            />

            <template v-else>
              <div
                class="cantileverFieldRow cantileverFieldRow--1"
                v-if="calculation.input.hasCantilever"
              >
                <div class="fields_grid">
                  <calc-number-input
                    :label="$t('Consola') + ' 1'"
                    v-model="cantilever1Span"
                    :step="0.1"
                    :min="0"
                    :max="4"
                    addon="m"
                  />
                </div>
              </div>

              <div class="fields_grid">
                <calc-number-input
                  v-for="(spanLength, i) in mainSpans"
                  :key="'spanDef-' + i"
                  :label="`${$t('Vão')} #${i + 1}`"
                  v-model="mainSpans[i]"
                  :min="0.5"
                  :max="20"
                  addon="m"
                />
              </div>

              <div
                class="cantileverFieldRow cantileverFieldRow--2"
                v-if="calculation.input.hasCantilever"
              >
                <div class="fields_grid">
                  <calc-number-input
                    :label="$t('Consola') + ' 2'"
                    v-model="cantilever2Span"
                    :step="0.1"
                    :min="0"
                    :max="4"
                    addon="m"
                  />
                </div>
              </div>
            </template>

            <template #illustration>
              <calc-illustration
                :totalSpans="totalSpans"
                :hasCantilever="calculation.input.hasCantilever"
                which="spans"
              />
            </template>
          </calc-form-item>
        </calc-form-card>

        <calc-form-card>
          <calc-form-item
            :title="$t('Espaçamento entre perfis')"
            title-style="inline"
          >
            <calc-number-input
              v-model="calculation.input.transversalDistance"
              :min="0.25"
              :max="10"
              addon="m"
            />

            <template #illustration>
              <img
                style="margin-top: -22px"
                src="@/assets/img/espacamento_entre_perfis.png"
              />
            </template>
          </calc-form-item>
        </calc-form-card>

        <calc-form-card>
          <template #controls>
            <b-switch
              v-model="isOpenExtraSupportWidthOptions"
              class="is-label_left"
              >{{ $t("Outras opções") }}</b-switch
            >
          </template>

          <calc-form-item title-style="inline">
            <template #title>
              {{ $t("Largura de apoios") }}
              <app-tooltip class="m-pad-0">
                <img src="@/assets/img/largura_apoios.png" />
              </app-tooltip>
            </template>
            <b-radio-group>
              <b-radio
                v-if="isOpenExtraSupportWidthOptions"
                native-value="1"
                v-model.number="calculation.input.supportWidth"
                >{{ $t("L. apoios") }} &lt; 135 mm</b-radio
              >
              <b-radio
                native-value="2"
                v-model.number="calculation.input.supportWidth"
                >{{ $t("L. apoios") }} &gt;=135 mm — {{ $t("L. apoios") }} &lt;
                200 mm</b-radio
              >
              <b-radio
                v-if="isOpenExtraSupportWidthOptions"
                native-value="3"
                v-model.number="calculation.input.supportWidth"
                >{{ $t("L. apoios") }} &gt;= 200mm</b-radio
              >
            </b-radio-group>
          </calc-form-item>
        </calc-form-card>
      </app-page-section>
    </div>

    <div class="steps_nav">
      <b-button
        class="steps_nav__btn"
        @click="$emit('next')"
        icon-right="arrow-right"
        size="is-medium"
        type="is-primary"
        >{{ $t("Continuar") }}</b-button
      >
    </div>
  </div>
</template>

<script>
import CalcIllustration from "./CalcIllustration";
import { mapState, mapActions, mapMutations, mapGetters } from "vuex";
import { Flag, withoutDuplicates, lastOf } from "@/utils";
import { calculatorHelpers } from "@/helpers";

const { getType2SpanRestraints } = calculatorHelpers;

export default {
  name: "CalcStepGeneral",

  components: {
    CalcIllustration,
  },

  data() {
    return {
      flag: new Flag(),

      isSpanBySpan: undefined,
      singleSpanValue: undefined,
      isOpenExtraSupportWidthOptions: undefined,

      defaultCantileverSpan: 0.2,

      mainSpans: [],
      cantilever1Span: null,
      cantilever2Span: null,

      useOpinionatedSpanRestraints: undefined,
    };
  },

  computed: {
    ...mapState(["countries", "user", "calculationData"]),

    ...mapState("calculator", [
      "calculation",
      "rawCalculationId",
      "isSpanRestraintsManuallyChanged",
    ]),

    ...mapGetters("calculator", ["load", "modelSpansLength"]),

    $modelSpans() {
      if (this.calculation.input.hasCantilever) {
        return [this.cantilever1Span, ...this.mainSpans, this.cantilever2Span];
      } else {
        return [...this.mainSpans];
      }
    },

    defaultMainSpan() {
      return this.mainSpans[this.mainSpans.length - 1];
    },

    totalSpans() {
      return this.calculation.input.modelSpans.length;
    },

    totalMainSpans: {
      get() {
        return this.mainSpans.length;
      },

      set(val) {
        const currentTotalMainSpans = this.mainSpans.length;

        let diff = val - currentTotalMainSpans;

        if (diff) {
          if (diff > 0) {
            //add
            this.mainSpans = [
              ...this.mainSpans,
              ...Array(diff).fill(this.defaultMainSpan),
            ];
          } else {
            //subtract
            this.mainSpans.splice(diff);
          }
        }
      },
    },

    formatIllustrationSrc() {
      const { direction, format } = this.calculation.input;
      return require(`@/assets/img/fachada_direction_${direction}_format_${format}.jpg`);
    },

    typeIllustrationSrc() {
      const { type } = this.calculation.input;
      return require(`@/assets/img/type_${type}.jpg`);
    },
  },

  watch: {
    async "calculation.input.type"(type) {
      // its retrieving from cache at this point, so it's instanteous.
      // no need to provide a loading indicator
      this.flag.create("skipReactivity");
      this.resetUi();
      const newCalculation = await this.getDefaultCalculation({ type });
      this.setCalculation({ val: newCalculation });
    },

    "calculation.input.modelSpans": {
      immediate: true,

      handler(modelSpans) {
        if (this.flag.test("$modelSpansTriggeredChange")) return;

        if (this.calculation.input.hasCantilever) {
          this.cantilever1Span = modelSpans[0];
          this.cantilever2Span = modelSpans[modelSpans.length - 1];
          this.mainSpans = modelSpans.slice(1, modelSpans.length - 1);

          this.isSpanBySpan = true;
        } else {
          this.cantilever1Span = null;
          this.cantilever2Span = null;
          this.mainSpans = [...modelSpans];

          if (withoutDuplicates(modelSpans).length > 1) {
            //exists more than one
            this.isSpanBySpan = true;
          } else {
            this.isSpanBySpan = false;
            this.singleSpanValue = modelSpans[0];
          }
        }
      },
    },

    "calculation.input.hasCantilever"(hasCantilever) {
      if (hasCantilever) {
        this.cantilever1Span = this.defaultCantileverSpan;
        this.cantilever2Span = this.defaultCantileverSpan;

        this.isSpanBySpan = true;
      } else {
        this.cantilever1Span = null;
        this.cantilever2Span = null;
      }
    },

    $modelSpans($modelSpans) {
      this.flag.create("$modelSpansTriggeredChange");
      this.calculation.input.modelSpans = $modelSpans;
    },

    singleSpanValue(singleSpanValue) {
      this.mainSpans = Array(this.totalMainSpans).fill(singleSpanValue);
    },

    "calculation.input.location"() {
      this.calculation.input.country = "";
    },

    isOpenExtraSupportWidthOptions(isOpenExtraSupportWidthOptions) {
      if (!isOpenExtraSupportWidthOptions) {
        this.calculation.input.supportWidth = 2;
      }
    },

    isSpanBySpan(isSpanBySpan) {
      if (!isSpanBySpan) {
        const { mainSpans, totalMainSpans } = this;
        const firstMainSpan = mainSpans[0];

        this.mainSpans = Array(totalMainSpans).fill(firstMainSpan);
        this.singleSpanValue = firstMainSpan;
      }
    },

    modelSpansLength() {
      if (!this.calculation.input.internal.isWindLoadsByBand) {
        [this.load("wind", "pressure"), this.load("wind", "suction")].forEach(
          (load) => {
            load.value[0][1] = this.modelSpansLength;
            load.value = [...load.value];
          }
        );
      }
    },

    totalSpans(totalSpans, oldTotalSpans) {
      if (this.flag.test("skipReactivity")) return;

      const { calculation } = this;
      const { hasCantilever } = calculation.input;

      const diff = totalSpans - oldTotalSpans;
      const added = diff > 0;

      //LOADS
      calculation.input.loads.forEach((load) => {
        if (
          load.type === "distributed" ||
          (load.case === "snow" && load.type !== "accumulation")
        ) {
          if (added) {
            const toAdd = Array(diff).fill(lastOf(load.value));

            if (hasCantilever) {
              //add before last span ( last span is cantilever)
              load.value.splice(-1, 0, ...toAdd);
            } else {
              load.value = [...load.value, ...toAdd];
            }
          } else {
            //subtracted

            if (hasCantilever) {
              load.value.splice(diff - 1, Math.abs(diff));
            } else {
              load.value.splice(diff, Math.abs(diff));
            }
          }
        }
      });

      // LOADS (INTERNAL)
      Object.values(calculation.input.internal.loads.points).forEach(
        (loadGroups) => {
          loadGroups.forEach((loadGroup) => {
            if (added) {
              if (loadGroups.length == 1) {
                if (loadGroup.span.length === oldTotalSpans) {
                  //select all that are added
                  loadGroup.span = Array(totalSpans)
                    .fill()
                    .map((_, i) => i + 1);
                }
              }
            } else {
              //subtracted
              loadGroup.span = loadGroup.span.filter((spanNum) => {
                return spanNum <= totalSpans;
              });
            }
          });
        }
      );
    },

    isSpanRestraintsManuallyChanged(isSpanRestraintsManuallyChanged) {
      this.useOpinionatedSpanRestraints = !isSpanRestraintsManuallyChanged;
    },
  },

  methods: {
    ...mapActions("calculator", ["getDefaultCalculation"]),

    ...mapMutations("calculator", [
      "setCalculation",
      "setIsSpanRestraintsManuallyChanged",
    ]),

    resetUi() {
      this.useOpinionatedSpanRestraints = true;
      this.$emit("clear-keep-alive-cache");
    },

    getOpinionatedSpanRestraints({
      direction,
      format,
      modelSpans,
      hasCantilever,
      type,
    }) {
      if (type === 2 || type === 3) {
        return getType2SpanRestraints({
          direction,
          format,
          modelSpans,
          hasCantilever,
        });
      } else {
        const { spanRestraintsDefaults } = this.calculationData.constants;

        const spanRestraintsDefaultsOfSelectedType =
          spanRestraintsDefaults[type];

        return {
          upperFibre: Array(this.mainSpans.length).fill(
            spanRestraintsDefaultsOfSelectedType.upperFibre
          ),
          lowerFibre: Array(this.mainSpans.length).fill(
            spanRestraintsDefaultsOfSelectedType.lowerFibre
          ),
        };
      }
    },

    getUnopinionatedSpanRestraints({ mainSpans, currentSpanRestraints }) {
      const newSpanRestraintsLength = mainSpans.length;
      const oldSpanRestraintsLength = currentSpanRestraints.upperFibre.length; //we could as well get the length from lowerFibre
      const diff = newSpanRestraintsLength - oldSpanRestraintsLength;

      if (!diff) {
        return currentSpanRestraints;
      }

      const added = diff > 0;

      const newSpanRestraints = {
        upperFibre: [],
        lowerFibre: [],
      };

      Object.entries(currentSpanRestraints).forEach(([type, val]) => {
        if (added) {
          newSpanRestraints[type] = [...val, ...Array(diff).fill(lastOf(val))];
        } else {
          //subtracted
          newSpanRestraints[type] = val.slice(0, diff);
        }
      });

      return newSpanRestraints;
    },

    startSpanRestraintsWatcher() {
      this.$watch(
        () => [
          this.calculation.input.format,
          this.calculation.input.direction,
          this.calculation.input.modelSpans,
          this.calculation.input.hasCantilever,
        ],

        ([format, direction, modelSpans, hasCantilever]) => {
          let newSpanRestraints;

          if (this.useOpinionatedSpanRestraints) {
            newSpanRestraints = this.getOpinionatedSpanRestraints({
              direction,
              format,
              modelSpans,
              hasCantilever,
              type: this.calculation.input.type,
            });
          } else {
            newSpanRestraints = this.getUnopinionatedSpanRestraints({
              mainSpans: this.mainSpans,
              currentSpanRestraints: this.calculation.input.spanRestraints,
            });
          }

          this.calculation.input.spanRestraints.upperFibre =
            newSpanRestraints.upperFibre;
          this.calculation.input.spanRestraints.lowerFibre =
            newSpanRestraints.lowerFibre;
        },
        {
          immediate: false,
        }
      );
    },
  },

  created() {
    this.isOpenExtraSupportWidthOptions =
      this.calculation.input.supportWidth !== 2;

    this.startSpanRestraintsWatcher();

    this.useOpinionatedSpanRestraints = !this.rawCalculationId;
  },
};
</script>
